import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Cloud, Edit, Plus, Printer, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const WeatherObservationModule = ({ theme, exerciseId }) => {
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingObservation, setEditingObservation] = useState(null);
  const [selectedObservations, setSelectedObservations] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    windDirection: '',
    windSpeed: '',
    visibility: '',
    presentWeather: '',
    skyCondition: '',
    temperature: '',
    dewPoint: '',
    altimeterSetting: '',
    remarks: ''
  });

  const [errors, setErrors] = useState({});

  // Dropdown options
  const presentWeatherOptions = [
    'Clear', 'Cloudy', 'Rain', 'Smoke', 'Snow', 'Hail', 'Ice Storm', 
    'Freezing Rain', 'High Winds', 'Extreme Cold', 'Extreme Heat', 
    'Thunderstorm', 'Tornado', 'Hurricane'
  ];

  const skyConditionOptions = [
    'Clear', 'Scattered', 'Broken', 'Overcast', 'Fog'
  ];

  // Load observations on component mount
  useEffect(() => {
    loadObservations();
  }, [exerciseId]);

  const loadObservations = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/weather-observations?exercise_id=${exerciseId}`);
      setObservations(response.data);
    } catch (error) {
      console.error('Error loading weather observations:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      time: '',
      location: '',
      windDirection: '',
      windSpeed: '',
      visibility: '',
      presentWeather: '',
      skyCondition: '',
      temperature: '',
      dewPoint: '',
      altimeterSetting: '',
      remarks: ''
    });
    setErrors({});
    setEditingObservation(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.location) newErrors.location = 'Location is required';
    
    // Wind direction validation (0-360 degrees)
    if (formData.windDirection && (isNaN(formData.windDirection) || formData.windDirection < 0 || formData.windDirection > 360)) {
      newErrors.windDirection = 'Wind direction must be 0-360 degrees';
    }
    
    // Wind speed validation (positive number)
    if (formData.windSpeed && (isNaN(formData.windSpeed) || formData.windSpeed < 0)) {
      newErrors.windSpeed = 'Wind speed must be a positive number';
    }
    
    // Visibility validation (positive number)
    if (formData.visibility && (isNaN(formData.visibility) || formData.visibility < 0)) {
      newErrors.visibility = 'Visibility must be a positive number';
    }
    
    // Temperature validation (numeric)
    if (formData.temperature && isNaN(formData.temperature)) {
      newErrors.temperature = 'Temperature must be a number';
    }
    
    // Dew point validation (numeric)
    if (formData.dewPoint && isNaN(formData.dewPoint)) {
      newErrors.dewPoint = 'Dew point must be a number';
    }
    
    // Altimeter setting validation (positive number)
    if (formData.altimeterSetting && (isNaN(formData.altimeterSetting) || formData.altimeterSetting < 0)) {
      newErrors.altimeterSetting = 'Altimeter setting must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const observationData = {
        ...formData,
        exercise_id: exerciseId,
        windDirection: formData.windDirection ? parseFloat(formData.windDirection) : null,
        windSpeed: formData.windSpeed ? parseFloat(formData.windSpeed) : null,
        visibility: formData.visibility ? parseFloat(formData.visibility) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        dewPoint: formData.dewPoint ? parseFloat(formData.dewPoint) : null,
        altimeterSetting: formData.altimeterSetting ? parseFloat(formData.altimeterSetting) : null
      };

      if (editingObservation) {
        await axios.put(`${API}/weather-observations/${editingObservation.id}`, observationData);
      } else {
        await axios.post(`${API}/weather-observations`, observationData);
      }

      resetForm();
      setShowForm(false);
      loadObservations();
    } catch (error) {
      console.error('Error saving weather observation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (observation) => {
    setFormData({
      date: observation.date || '',
      time: observation.time || '',
      location: observation.location || '',
      windDirection: observation.windDirection?.toString() || '',
      windSpeed: observation.windSpeed?.toString() || '',
      visibility: observation.visibility?.toString() || '',
      presentWeather: observation.presentWeather || '',
      skyCondition: observation.skyCondition || '',
      temperature: observation.temperature?.toString() || '',
      dewPoint: observation.dewPoint?.toString() || '',
      altimeterSetting: observation.altimeterSetting?.toString() || '',
      remarks: observation.remarks || ''
    });
    setEditingObservation(observation);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this weather observation?')) return;

    try {
      setLoading(true);
      await axios.delete(`${API}/weather-observations/${id}`);
      loadObservations();
    } catch (error) {
      console.error('Error deleting weather observation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (observation = null) => {
    const printContent = observation ? 
      generateSingleObservationPrint(observation) : 
      generateMultipleObservationsPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Weather Observations</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .observation { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 150px; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print {
              .observation { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const generateSingleObservationPrint = (observation) => {
    return `
      <div class="header">
        <h1>Weather Observation Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
      </div>
      <div class="observation">
        <div class="field"><span class="label">Date:</span> ${observation.date || 'N/A'}</div>
        <div class="field"><span class="label">Time:</span> ${observation.time || 'N/A'}</div>
        <div class="field"><span class="label">Location:</span> ${observation.location || 'N/A'}</div>
        <div class="field"><span class="label">Wind Direction:</span> ${observation.windDirection ? observation.windDirection + '°' : 'N/A'}</div>
        <div class="field"><span class="label">Wind Speed:</span> ${observation.windSpeed ? observation.windSpeed + ' km/h' : 'N/A'}</div>
        <div class="field"><span class="label">Visibility:</span> ${observation.visibility ? observation.visibility + ' km' : 'N/A'}</div>
        <div class="field"><span class="label">Present Weather:</span> ${observation.presentWeather || 'N/A'}</div>
        <div class="field"><span class="label">Sky Condition:</span> ${observation.skyCondition || 'N/A'}</div>
        <div class="field"><span class="label">Temperature:</span> ${observation.temperature ? observation.temperature + '°C' : 'N/A'}</div>
        <div class="field"><span class="label">Dew Point:</span> ${observation.dewPoint ? observation.dewPoint + '°C' : 'N/A'}</div>
        <div class="field"><span class="label">Altimeter Setting:</span> ${observation.altimeterSetting ? observation.altimeterSetting + ' hPa' : 'N/A'}</div>
        <div class="field"><span class="label">Remarks:</span> ${observation.remarks || 'None'}</div>
      </div>
    `;
  };

  const generateMultipleObservationsPrint = () => {
    const selectedObs = selectedObservations.length > 0 ? 
      observations.filter(obs => selectedObservations.includes(obs.id)) : 
      observations;
    
    return `
      <div class="header">
        <h1>Weather Observations Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
        <p>${selectedObs.length} observation(s)</p>
      </div>
      ${selectedObs.map(obs => generateSingleObservationPrint(obs)).join('')}
    `;
  };

  const toggleObservationSelection = (id) => {
    setSelectedObservations(prev => 
      prev.includes(id) 
        ? prev.filter(obsId => obsId !== id)
        : [...prev, id]
    );
  };

  const formatDateTime = (date, time) => {
    if (!date) return 'N/A';
    const dateStr = new Date(date).toLocaleDateString();
    return time ? `${dateStr} ${time}` : dateStr;
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Weather Observations
            </h1>
            <p className="text-white text-sm">
              Record and manage weather observations for incident planning and response
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Observation
            </Button>
            {selectedObservations.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedObservations.length})
              </Button>
            )}
            {observations.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Observation Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingObservation ? 'Edit Weather Observation' : 'Add Weather Observation'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Date and Time Row */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.date ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Time *
                </label>
                <input
                  type="text" pattern="[0-2][0-9]:[0-5][0-9]" placeholder="HH:MM (24-hour)" maxLength="5"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.time ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.time && <p className="text-red-500 text-xs mt-1">{errors.time}</p>}
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Location *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="e.g., Emergency Operations Center, Field Station Alpha"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.location ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
            </div>

            {/* Wind Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Wind Direction (degrees, 0-360)
                </label>
                <input
                  type="number"
                  min="0"
                  max="360"
                  value={formData.windDirection}
                  onChange={(e) => setFormData({...formData, windDirection: e.target.value})}
                  placeholder="e.g., 270"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.windDirection ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.windDirection && <p className="text-red-500 text-xs mt-1">{errors.windDirection}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Wind Speed (kts)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.windSpeed}
                  onChange={(e) => setFormData({...formData, windSpeed: e.target.value})}
                  placeholder="e.g., 15.5"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.windSpeed ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.windSpeed && <p className="text-red-500 text-xs mt-1">{errors.windSpeed}</p>}
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Visibility (kts)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.visibility}
                onChange={(e) => setFormData({...formData, visibility: e.target.value})}
                placeholder="e.g., 10.0"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.visibility ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.visibility && <p className="text-red-500 text-xs mt-1">{errors.visibility}</p>}
            </div>

            {/* Weather Conditions */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Present Weather
                </label>
                <select
                  value={formData.presentWeather}
                  onChange={(e) => setFormData({...formData, presentWeather: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  <option value="">Select weather condition</option>
                  {presentWeatherOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Sky Condition
                </label>
                <select
                  value={formData.skyCondition}
                  onChange={(e) => setFormData({...formData, skyCondition: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  <option value="">Select sky condition</option>
                  {skyConditionOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Temperature Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Temperature (°C)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => setFormData({...formData, temperature: e.target.value})}
                  placeholder="e.g., 22.5"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.temperature ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.temperature && <p className="text-red-500 text-xs mt-1">{errors.temperature}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Dew Point (°C)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.dewPoint}
                  onChange={(e) => setFormData({...formData, dewPoint: e.target.value})}
                  placeholder="e.g., 18.2"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.dewPoint ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.dewPoint && <p className="text-red-500 text-xs mt-1">{errors.dewPoint}</p>}
              </div>
            </div>

            {/* Altimeter Setting */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Altimeter Setting (hPa)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.altimeterSetting}
                onChange={(e) => setFormData({...formData, altimeterSetting: e.target.value})}
                placeholder="e.g., 1013.25"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.altimeterSetting ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.altimeterSetting && <p className="text-red-500 text-xs mt-1">{errors.altimeterSetting}</p>}
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Remarks
              </label>
              <textarea
                rows="3"
                value={formData.remarks}
                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                placeholder="Additional information and other details of interest to the Incident Commander..."
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {loading ? 'Saving...' : editingObservation ? 'Update Observation' : 'Save Observation'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Observations List */}
      <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700">
        {loading && !showForm ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-white">Loading weather observations...</p>
          </div>
        ) : observations.length === 0 ? (
          <div className="text-center py-12">
            <Cloud className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-orange-500 mb-2">No Weather Observations</h3>
            <p className="text-white mb-4">
              Start by adding your first weather observation for this exercise.
            </p>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Observation
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedObservations.length === observations.length}
                      onChange={(e) => {
                        setSelectedObservations(
                          e.target.checked ? observations.map(obs => obs.id) : []
                        );
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Date/Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Weather
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Wind
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Temp/Dew
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-600">
                {observations.map((observation) => (
                  <tr key={observation.id} className={`hover:bg-gray-700 ${
                    selectedObservations.includes(observation.id) ? 'bg-orange-900/30' : ''
                  }`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedObservations.includes(observation.id)}
                        onChange={() => toggleObservationSelection(observation.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {formatDateTime(observation.date, observation.time)}
                    </td>
                    <td className="px-6 py-4 text-sm text-white">
                      {observation.location || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-white">
                      <div>{observation.presentWeather || 'N/A'}</div>
                      <div className="text-xs text-gray-300">{observation.skyCondition || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white">
                      {observation.windDirection !== null && observation.windSpeed !== null 
                        ? `${observation.windDirection}° at ${observation.windSpeed} km/h`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-white">
                      {observation.temperature !== null 
                        ? `${observation.temperature}°C` 
                        : 'N/A'}
                      {observation.dewPoint !== null && (
                        <div className="text-xs text-gray-300">Dew: ${observation.dewPoint}°C</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(observation)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handlePrint(observation)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(observation.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// ICSOrganizationChart is now imported from ./components/ICSOrganizationChart.js

// Simple Sketch Component for Safety Message Plan

export default WeatherObservationModule;
